import { useState, useEffect } from "react";
import {
  ArrowLeft,
  ArrowRight,
  Book,
  Info,
  PanelLeftClose,
  PanelLeftOpen,
  Pencil,
  RefreshCw,
} from "lucide-react";
import Markdown from "react-markdown";
import { tC } from "../../utils/TranslateWithContentLanguage";
import RichTextEditor from "../tool_components/RichTextEditor";

import SingleVocabularyPDF from "./SingleVocabularyPdf";
import PdfDownloadButton from "../pdf/PdfDownloadButton";
import { Button, Text } from "@core/index";
import {
  getGetUserVocabularyListsQueryKey,
  getGetVocabularyListQueryKey,
  useUpdateVocabularyList,
} from "@packages/api/madlenAPI";
import { VocabularyListItem } from "@packages/api/madlenAPI.schemas";
import { useQueryClient } from "@tanstack/react-query";
import { useAppSelector } from "@packages/store/hooks";
import PdfCustomizationModal from "@features/common/components/pdf-customization-modal";
interface VocabularyWord {
  id: number;
  word: string;
  usage: string;
  explanation: string;
  imageUrl?: string;
}

const SingleVocabulary = ({
  id,
  isEditable,
  vocabulary,
  courseName,
  gradeLevel,
  topic,
  imageUrl,
  author,
  createdAt,
  onCreateAgain,
  contentLanguage,
}: {
  id: number;
  isEditable: boolean;
  vocabulary: VocabularyWord[] | undefined;
  gradeLevel?: number;
  courseName?: string;
  topic?: string;
  imageUrl?: string;
  onCreateAgain?: () => void;
  author?: string;
  createdAt?: string;
  contentLanguage: string;
}) => {
  const [showFlashcard, setShowFlashcard] = useState(false);
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [showWordList, setShowWordList] = useState(false);
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [editWord, setEditWord] = useState<string>("");
  const [editDescription, setEditDescription] = useState<string>("");
  const [editUsage, setEditUsage] = useState<string>("");
  const [isSaving, setIsSaving] = useState(false);

  const queryClient = useQueryClient();
  const { mutateAsync: editVocabulary } = useUpdateVocabularyList();
  const [showPdfCustomizationModal, setShowPdfCustomizationModal] =
    useState(false);
  const customizationSettings = useAppSelector(
    (state) => state.user.teacher?.preferences?.pdfOptions,
  );
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!showFlashcard) return;

      if (e.key === "ArrowUp") {
        e.preventDefault();
        setCurrentCardIndex((prev) =>
          prev === 0 ? vocabulary!.length - 1 : prev - 1,
        );
        setIsFlipped(false);
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        setCurrentCardIndex((prev) =>
          prev === vocabulary!.length - 1 ? 0 : prev + 1,
        );
        setIsFlipped(false);
      } else if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
        e.preventDefault();
        setIsFlipped((prev) => !prev);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [showFlashcard, vocabulary]);

  useEffect(() => {
    if (!vocabulary) return;

    vocabulary.forEach((word) => {
      if (word.imageUrl) {
        const img = new Image();
        img.src = word.imageUrl;
      }
    });
  }, [vocabulary]);

  if (!vocabulary) {
    return (
      <div className="flex items-center justify-center min-h-[400px] bg-gray-50 dark:bg-gray-900/20 rounded-2xl p-8">
        <div className="animate-spin w-8 h-8 border-4 border-cyan-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  const nextCard = () => {
    setIsFlipped(false);
    setCurrentCardIndex((prev) =>
      prev === vocabulary.length - 1 ? 0 : prev + 1,
    );
  };

  const handleEditClick = (index: number) => {
    setEditWord(vocabulary[index].word);
    setEditDescription(vocabulary[index].explanation);
    setEditUsage(vocabulary[index].usage);
    setEditIndex(index);
  };

  const handleCancelEdit = () => {
    setEditIndex(null);
  };

  const handleSaveEdit = async () => {
    if (editIndex !== null && vocabulary) {
      setIsSaving(true);
      const updatedVocabulary: VocabularyListItem[] = vocabulary.map(
        (word, idx) =>
          idx === editIndex
            ? {
                ...word,
                word: editWord,
                explanation: editDescription,
                usage: editUsage,
              }
            : word,
      );
      const updateData = {
        id: id,
        data: {
          vocabulary: updatedVocabulary,
        },
      };
      await editVocabulary(updateData);
      await queryClient.invalidateQueries({
        queryKey: getGetUserVocabularyListsQueryKey(),
      });
      await queryClient.invalidateQueries({
        queryKey: getGetVocabularyListQueryKey(id),
      });
      setIsSaving(false);
      setEditIndex(null);
    }
  };

  return (
    <div
      className="container max-w-7xl mx-auto"
      data-testid="vocabulary-container"
    >
      {/* View Toggle */}
      <div
        className="max-w-4xl mx-auto mb-8 flex flex-col sm:flex-row items-center justify-between gap-4 w-full mt-2"
        data-testid="vocabulary-view-toggle"
      >
        <Button
          onClick={() => setShowFlashcard(!showFlashcard)}
          variant="solid"
          size="lg"
          rounded="xl"
          textColor="text-white"
          bgColor="bg-gradient-to-r from-cyan-600 to-cyan-500 hover:from-cyan-700 hover:to-cyan-600"
          className="w-full sm:w-3/5 font-medium shadow-lg hover:shadow-xl transform hover:-translate-y-0.5  text-lg"
        >
          {showFlashcard
            ? tC(contentLanguage, "vocabularies.switchToList")
            : tC(contentLanguage, "vocabularies.switchToFlashcard")}
        </Button>
        {onCreateAgain && (
          <Button
            onClick={onCreateAgain}
            variant="solid"
            size="md"
            textColor="text-white dark:text-white"
            bgColor="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 w-full sm:w-2/5 py-3 text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 duration-200 "
            rounded="xl"
          >
            <RefreshCw size={18} />
            <span>{tC(contentLanguage, "common.createAnother")}</span>
          </Button>
        )}

        <PdfDownloadButton
          pdfTitle={topic || ""}
          openCustomizationModal={() => setShowPdfCustomizationModal(true)}
          document={
            <SingleVocabularyPDF
              customizationSettings={customizationSettings}
              author={author || ""}
              createdAt={createdAt || ""}
              vocabulary={vocabulary}
              courseName={courseName || ""}
              gradeLevel={gradeLevel || 0}
              topic={topic || ""}
              imageUrl={""}
            />
          }
          btnTheme="flex-1 w-full sm:w- 2/5 py-3 text-white rounded-xl font-medium text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 px-6"
        />
      </div>

      {showFlashcard ? (
        <div className="max-w-7xl mx-auto">
          {/* Word list toggle button - visible on all screens */}
          <div className="flex items-center gap-2 mb-4">
            <Button
              onClick={(e) => {
                e.stopPropagation();
                setShowWordList(!showWordList);
              }}
              variant="solid"
              size="md"
              rounded="xl"
              textColor="text-white"
              bgColor="bg-gradient-to-r from-cyan-500 to-blue-500"
              className="hover:shadow-lg"
            >
              {showWordList ? (
                <PanelLeftClose size={20} />
              ) : (
                <PanelLeftOpen size={20} />
              )}
            </Button>

            <div className="relative inline-block group">
              <Button
                onClick={(e) => e.stopPropagation()}
                variant="solid"
                size="icon"
                rounded="full"
                bgColor="hover:bg-gray-100 dark:hover:bg-gray-700"
                aria-label="Information"
              >
                <Info size={16} className="text-gray-400" />
              </Button>

              <div
                className="invisible group-hover:visible absolute top-1/2 -translate-y-1/2 left-full ml-4 w-48 p-2 bg-gray-900 dark:bg-gray-800 text-white dark:text-gray-100 text-sm rounded-lg shadow-lg z-[9999]"
                role="tooltip"
              >
                {tC(
                  contentLanguage,
                  "vocabularies.tutorial.flipCard",
                  "Use ← → arrow keys to flip the card",
                ) +
                  " " +
                  tC(contentLanguage, "common.and") +
                  " " +
                  tC(
                    contentLanguage,
                    "vocabularies.tutorial.navigation",
                    "Use ↑ ↓ arrow keys to navigate between cards",
                  )}

                <div className="absolute top-1/2 -translate-y-1/2 -left-1 w-2 h-2 bg-gray-900 dark:bg-gray-800 transform rotate-45"></div>
              </div>
            </div>
          </div>

          {/* Fixed flex layout */}
          <div className="flex flex-col md:flex-row gap-8">
            {/* Word List - Toggle visibility on all screens */}
            <div className={`md:w-1/3 ${showWordList ? "block" : "hidden"}`}>
              <div className="grid grid-cols-1 gap-4 h-[500px] overflow-y-auto scrollbar-thin scrollbar-thumb-cyan-500 scrollbar-track-transparent pr-2 border border-cyan-500/20 rounded-xl">
                {vocabulary.map((word, idx) => (
                  <Button
                    key={idx}
                    onClick={() => {
                      setCurrentCardIndex(idx);
                      setIsFlipped(false);
                    }}
                    variant="solid"
                    size="md"
                    rounded="xl"
                    textColor={
                      currentCardIndex === idx
                        ? "text-cyan-600 dark:text-cyan-400"
                        : ""
                    }
                    bgColor={
                      currentCardIndex === idx
                        ? "bg-cyan-100 dark:bg-cyan-900"
                        : "bg-white/80 dark:bg-gray-800/80 hover:bg-cyan-50 dark:hover:bg-cyan-900/50"
                    }
                  >
                    <Markdown>{word.word}</Markdown>
                  </Button>
                ))}
              </div>
            </div>

            {/* Flashcard */}
            <div className="flashcard flex-1">
              <div
                className={`p-8 w-full flex flex-col items-center justify-center min-h-[400px] cursor-pointer rounded-3xl shadow-2xl border border-cyan-500/20 dark:border-cyan-900/40 relative overflow-hidden ${
                  vocabulary[currentCardIndex].imageUrl
                    ? ""
                    : "bg-cyan-500/10 dark:bg-cyan-900/10"
                }`}
                onClick={() => setIsFlipped(!isFlipped)}
                style={{
                  height: "500px",
                }}
              >
                {vocabulary[currentCardIndex].imageUrl && (
                  <>
                    <img
                      src={vocabulary[currentCardIndex].imageUrl}
                      alt=""
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/85 to-black/50" />
                  </>
                )}
                <div
                  className={`absolute inset-0 flex items-center justify-center transition-all duration-300 ${isFlipped ? "opacity-0 pointer-events-none" : "opacity-100"}`}
                >
                  {/* Front of card */}
                  <div className="flex flex-col items-center justify-center">
                    <h2
                      className={`text-4xl font-bold mb-8 text-center ${
                        vocabulary[currentCardIndex].imageUrl
                          ? "text-white"
                          : "bg-gradient-to-r from-cyan-500 to-blue-500 bg-clip-text text-transparent"
                      }`}
                    >
                      <Markdown>{vocabulary[currentCardIndex].word}</Markdown>
                    </h2>
                    <div
                      className={`text-lg flex items-center gap-2 px-4 py-2 rounded-full shadow-inner ${
                        vocabulary[currentCardIndex].imageUrl
                          ? "text-white bg-white/20"
                          : "text-gray-600 dark:text-gray-300 bg-white/50 dark:bg-gray-700/50"
                      }`}
                    >
                      <RefreshCw className="w-5 h-5 animate-spin-slow" />
                      {tC(contentLanguage, "vocabularies.tapToReveal")}
                    </div>
                  </div>
                </div>

                <div
                  className={`absolute inset-0 flex items-center justify-center transition-all duration-300 ${!isFlipped ? "opacity-0 pointer-events-none" : "opacity-100"}`}
                >
                  {/* Back of card */}
                  <div className="flex flex-col items-center justify-center p-4">
                    <div className="max-w-md space-y-8">
                      <div
                        className={`p-6 rounded-2xl shadow-inner ${
                          vocabulary[currentCardIndex].imageUrl
                            ? "bg-black/40"
                            : "bg-white/50 dark:bg-gray-700/50"
                        }`}
                      >
                        <p
                          className={`text-xl text-center leading-relaxed ${
                            vocabulary[currentCardIndex].imageUrl
                              ? "text-white"
                              : "text-gray-700 dark:text-gray-300"
                          }`}
                        >
                          <Markdown>
                            {vocabulary[currentCardIndex].explanation}
                          </Markdown>
                        </p>
                      </div>
                      <div className="w-24 h-1 bg-gradient-to-r from-cyan-500 to-blue-500 mx-auto rounded-full"></div>
                      <div
                        className={`p-6 rounded-2xl ${
                          vocabulary[currentCardIndex].imageUrl
                            ? "bg-black/30"
                            : "bg-gradient-to-r from-cyan-500/10 to-blue-500/10"
                        }`}
                      >
                        <p
                          className={`italic text-lg text-center ${
                            vocabulary[currentCardIndex].imageUrl
                              ? "text-white"
                              : "text-cyan-600 dark:text-cyan-400"
                          }`}
                        >
                          <Markdown>
                            {vocabulary[currentCardIndex].usage}
                          </Markdown>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* Navigation buttons */}
              <div className="flex justify-center mt-8 gap-6">
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsFlipped(false);
                    setCurrentCardIndex((prev) =>
                      prev === 0 ? vocabulary.length - 1 : prev - 1,
                    );
                  }}
                  variant="solid"
                  size="lg"
                  rounded="xl"
                  textColor="text-white"
                  bgColor="bg-gradient-to-r from-cyan-500 to-blue-500"
                  className="hover:shadow-lg hover:shadow-cyan-500/25 dark:hover:shadow-cyan-400/25 hover:-translate-y-0.5"
                >
                  <ArrowLeft size={20} />
                </Button>

                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    nextCard();
                  }}
                  variant="solid"
                  size="lg"
                  rounded="xl"
                  textColor="text-white"
                  bgColor="bg-gradient-to-r from-cyan-500 to-blue-500"
                  className="hover:shadow-lg hover:shadow-cyan-500/25 dark:hover:shadow-cyan-400/25 hover:-translate-y-0.5"
                >
                  <ArrowRight size={20} />
                </Button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        // List View
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-h-[80vh] scrollbar-thin scrollbar-thumb-cyan-500 scrollbar-track-transparent pr-2">
          {vocabulary?.map((word, index) => (
            <div
              key={index}
              className="relative bg-white dark:bg-gray-900 rounded-2xl shadow-lg dark:shadow-2xl border border-gray-200 dark:border-gray-800 overflow-hidden transition-colors duration-300"
            >
              {editIndex === null && isEditable && (
                <Button
                  onClick={() => handleEditClick(index)}
                  variant="solid"
                  size="md"
                  rounded="xl"
                  textColor="text-white dark:text-white"
                  bgColor="bg-gradient-to-r from-cyan-500 to-blue-500 dark:from-cyan-700 dark:to-blue-700"
                  className="absolute top-2 right-2 p-2"
                >
                  <Pencil size={16} />
                </Button>
              )}
              <div className="px-8 py-6">
                {editIndex === index ? (
                  <div className="flex flex-col gap-2">
                    <input
                      type="text"
                      value={editWord}
                      onChange={(e) => setEditWord(e.target.value)}
                      className="w-full p-2 border rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-700 transition-colors duration-200"
                    />
                    <div className="bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-700 rounded transition-colors duration-200">
                      <RichTextEditor
                        value={editDescription}
                        onChange={(value) => setEditDescription(value)}
                      />
                    </div>
                    <div className="bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-700 rounded transition-colors duration-200">
                      <RichTextEditor
                        value={editUsage}
                        onChange={(value) => setEditUsage(value)}
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={handleSaveEdit}
                        variant="solid"
                        size="md"
                        rounded="xl"
                        textColor="text-white dark:text-white"
                        bgColor="bg-gradient-to-r from-cyan-500 to-blue-500 dark:from-cyan-700 dark:to-blue-700"
                        disabled={isSaving}
                      >
                        {isSaving
                          ? tC(contentLanguage, "common.saving")
                          : tC(contentLanguage, "common.save")}
                      </Button>
                      <Button
                        onClick={handleCancelEdit}
                        variant="solid"
                        size="md"
                        rounded="xl"
                        textColor="text-white dark:text-white"
                        bgColor="bg-gradient-to-r from-gray-500 to-gray-600 dark:from-gray-700 dark:to-gray-800"
                      >
                        {tC(contentLanguage, "common.cancel")}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="flex items-center space-x-4 mb-4">
                      <span className="p-2 rounded-xl bg-cyan-100 dark:bg-cyan-900/50 text-cyan-600 dark:text-cyan-400">
                        <Book size={24} />
                      </span>
                      <Text variant={"h2"}>
                        <Markdown>{word.word}</Markdown>
                      </Text>
                    </div>
                    <Text variant={"p"} size={"base"} className="mb-4">
                      <Markdown>{word.explanation}</Markdown>
                    </Text>
                    <Text
                      variant={"p"}
                      size={"base"}
                      className="italic !text-cyan-600 dark:!text-cyan-400"
                    >
                      <Markdown>{word.usage}</Markdown>
                    </Text>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      {/* PDF Customization Modal */}
      <PdfCustomizationModal
        show={showPdfCustomizationModal}
        onClose={() => setShowPdfCustomizationModal(false)}
        hideSchoolLogoOptions
        hideGeneratedImageOptions
        documentComponent={
          <SingleVocabularyPDF
            customizationSettings={customizationSettings}
            author={author || ""}
            createdAt={createdAt || ""}
            vocabulary={vocabulary}
            courseName={courseName || ""}
            gradeLevel={gradeLevel || 0}
            topic={topic || ""}
            imageUrl={""}
          />
        }
      />
    </div>
  );
};

export default SingleVocabulary;
